import { ResizeObserver } from '@juggle/resize-observer';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { animated, useSprings } from '@react-spring/web';
import { useGesture } from '@use-gesture/react';
import {
    Children,
    cloneElement,
    isValidElement,
    MouseEvent,
    ReactElement,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import useMeasure from 'react-use-measure';

export type ImgElementProps = {
    alt?: string;
    children?: ReactElement<ImgElementProps>[];
    loading?: 'lazy' | 'eager' | 'auto';
    onDragStart?: (e: MouseEvent<Element, MouseEvent>) => void;
    src?: string;
};

type Props = {
    /* index of the active slide */
    activeSlideIndex?: number;
    /* time in ms */
    autoScroll?: number;
    /* the slides */
    children?: ReactElement[];
    /* show dots */
    hasDots?: boolean;
    /* triggered on slide change */
    onChange?: (newSlideIndex: number) => void;
};

// #region helper functions
/**
 * Get the circular index of an array, that is offset from the active slide.
 * note: It walks the array backwards of what you would intuitively think.
 * example:
 * Given there are 5 slides [0,1,2,3,4], and the current slide is 0
 * If the offset is -1, the return will be 1.
 * If the offset is -2, the return will be 2.
 * If the offset is -3, the return will be 3.
 * If the offset is 1, the return will be 4.
 * If the offset is 2, the return will be 3.
 * If the offset is 5, the return will be 2.
 * If the offset is 12, the return will be 3.
 * @see https://stackoverflow.com/a/54427125/2529736
 * @param {number} offset - the offset from the active slide
 * @param {number} activeSlide - the current active slide
 * @param {number} slideCount - the number of slides
 * @returns {number} index of the array that is offset from the active slide
 */
const getCircularIndex = (offset: number, activeSlide: number, slideCount: number): number =>
    (((activeSlide - offset) % slideCount) + slideCount) % slideCount;

/**
 * Returns an offset for an array index, given the current active slide index.
 * example:
 * If there are 5 slides [0,1,2,3,4], and the current slide is 0
 * getting to slide 4 can be done by animating left 1 slide, or right 4 slides
 * so slide 4 will be given an offset of -1 placing it to the left of slide 0
 * @param {number} index
 * @param {number} currentSlide
 * @param {number} slides
 * @returns {number} the slide offest
 */
const getSlideOffset = (index: number, currentSlide: number, slides: number): number => {
    if (index === currentSlide) {
        return 0;
    }

    // note: walk distances are backwards from what you would intuitively think
    // b/c the getCircularIndex walks the array backwards...

    // walk array left to find 'distance'
    let left = 0;
    while (getCircularIndex(left, currentSlide, slides) !== index && Math.abs(left) < slides) {
        left--;
    }

    // walk array right to find 'distance'
    let right = 0;
    while (getCircularIndex(right, currentSlide, slides) !== index && Math.abs(right) < slides) {
        right++;
    }

    // return the smallest distance as the offset
    let offset = left;
    if (Math.abs(left) > Math.abs(right)) {
        offset = right;
    }

    // again, the offset is backwards from what you would intuitively think
    return offset * -1;
};

/**
 * Overrides the loading attribute of an image element to 'auto' if the original value is 'lazy'.
 * @param {ReactElement} child - The child element to override the loading attribute.
 * @return {ReactElement} The modified child element.
 */
const overrideImgLoadingAttr = (child: ReactElement<ImgElementProps>): ReactElement => {
    if (child.type === 'img' && child.props.loading === 'lazy') {
        return cloneElement(child, {
            ...child.props,
            loading: 'auto',
        });
    }
    return child;
};

/**
 * Generates a new React element with the onDragStart event overridden to prevent the default behavior.
 * This prevent the image from being clicked and dragged outside of the x-axis slide
 * @param {ReactElement} child - The child element to override.
 * @return {ReactElement} - The updated child element.
 */
const overrideImgOnDragStart = (child: ReactElement<ImgElementProps>): ReactElement => {
    if (child.type === 'img') {
        return cloneElement(child, {
            ...child.props,
            onDragStart: (e) => e.preventDefault(),
        });
    }
    return child;
};

/**
 * Overrides the img tag props of a React element.
 * loading="lazy" will become loading="auto"
 * onDragStart will become onDragStart={(e) => e.preventDefault()}
 * @param {ReactElement} child - The child element to override the image props.
 * @return {ReactElement} The modified child element with overridden image props.
 */
const overrideImgProps = (child: ReactElement<ImgElementProps>): ReactElement => {
    if (child.type !== 'img') {
        return child;
    }
    return overrideImgOnDragStart(overrideImgLoadingAttr(child));
};

/**
 * Recursively map react children
 * @param children
 * @param fn
 * @returns {ReactElement}
 */
const recursiveMapReactChildren = (
    child: ReactElement<ImgElementProps>,
    fn: (child: ReactElement) => ReactElement
): ReactElement => {
    if (!isValidElement(child)) {
        return child;
    }

    let mapped = fn(child) as ReactElement<ImgElementProps>;

    if (mapped.props.children) {
        const children = Children.map(mapped.props.children, (c) => recursiveMapReactChildren(c, fn));
        const props = {
            ...mapped.props,
            children,
        };
        mapped = cloneElement(mapped, props);
    }

    return mapped;
};

// #endregion helper functions

export const Slider = ({
    activeSlideIndex = 0,
    autoScroll = 0,
    children = [],
    hasDots = false,
    onChange = () => null,
}: Props) => {
    const [currentSlide, setCurrentSlide] = useState(activeSlideIndex);
    const [previousSlide, setPreviousSlide] = useState(currentSlide);
    const [isDragging, setIsDragging] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const [sliderRef, { width }] = useMeasure({ polyfill: ResizeObserver });
    const autoScrollInterval = useRef<ReturnType<typeof setInterval>>(null);

    // loops through slides and overrides img tag loading and onDragStart attributes
    const slides = children.map((child, index) => {
        const clonedChild = recursiveMapReactChildren(child, overrideImgProps);
        return (
            <div
                className="h-full w-full"
                key={index}
            >
                {clonedChild}
            </div>
        );
    });

    // calls onClick on the current slide
    const onClickSlide = (slideIndex: number) => {
        slides[slideIndex].props.children.props.onClick && slides[slideIndex].props.children.props.onClick();
    };

    // #region slide spring
    type SlidePositionsProps = {
        index: number;
        xDelta?: number;
    };
    const setSlidePositions = useCallback(
        ({ index, xDelta }: SlidePositionsProps) => {
            // show all slides if user is dragging
            let opacity = isDragging ? 1 : 0;
            // show animation if user is dragging
            let immediate = !isDragging;

            // make sure current and previous slides are shown
            // this is used for when the user is not dragging, so dots or autoScroll atm
            if (index === currentSlide || index === previousSlide) {
                opacity = 1;
                immediate = false;
            }

            // set initial slide offset
            let offset = getSlideOffset(index, currentSlide, slides.length);
            // add any offset from dragging
            if (isDragging && !isNaN(xDelta)) {
                offset += xDelta / width;
            }

            return { immediate, offset, opacity };
        },
        [currentSlide, isDragging, previousSlide, slides.length, width]
    );

    const [springProps, springsApi] = useSprings(slides.length, (index: number) => setSlidePositions({ index }));

    // initialize springs
    useEffect(() => {
        springsApi.start((index) => setSlidePositions({ index }));
    });

    // trigger animation on slide changed, via prop, dots, or autoScroll
    useEffect(() => {
        springsApi.update((index) => setSlidePositions({ index })).start();
        onChange(currentSlide);
    }, [currentSlide, onChange, setSlidePositions, springsApi]);
    // #endregion slide spring

    // #region gesture controls
    const gestureBinds = useGesture(
        {
            onClick: () => {
                if (isDragging) {
                    setIsDragging(false);
                    return;
                }
                onClickSlide(currentSlide);
            },

            onDrag: ({ movement: [xDelta] }) => {
                // if we don't have a width, we can't do anything
                if (!width) {
                    return;
                }

                // update all springs with the drag xDelta
                springsApi.start((index) => setSlidePositions({ index, xDelta }));
            },
            onDragEnd: ({ distance: [xDistance], movement: [xDelta], velocity: [xVelocity] }) => {
                setIsDragging(false);

                const draggedFarEnough = xDistance > width / 4;
                const draggedFastEnough = xVelocity > 0.5;
                if (draggedFarEnough || draggedFastEnough) {
                    // how many slides to move left/right
                    const delta = Math.abs(xDelta) > width ? xDelta : (xDelta * width) / Math.abs(xDelta);
                    const slideMoveCount = Math.round(delta / width);
                    // get the index of the slide we want to go to
                    let slide = getCircularIndex(slideMoveCount, currentSlide, slides.length);

                    setPreviousSlide(currentSlide);
                    setCurrentSlide(slide);
                }
            },
            onDragStart: () => {
                setIsDragging(true);
            },
            onHover: ({ active }) => {
                if (active) {
                    setIsHovering(true);
                } else {
                    setIsHovering(false);
                }
            },
        },
        {
            drag: {
                // specifies the axis slides drag on
                axis: 'x',
                // delays the drag animation and mobile scroll prevention by milliseconds
                delay: 200,
                // prevents lightbox from opening after dragging a slide
                filterTaps: false,
                // prevents mobile scrolling after user taps and holds for delay time (refer to delay prop)
                preventScroll: true,
                // specifies what axis mobile scroll is prevented on
                preventScrollAxis: 'y',
            },
        }
    );
    // #endregion gesture controls

    // #region dots
    const dots = () => {
        const dots = [];
        // for loop rather than a map here b/c it'll make it easier to add
        // a feature to show multiple slides in the future, if needed
        for (let index = 0; index < slides.length; index++) {
            dots.push(
                <div
                    className={cn('mx-2xs my-0 inline-block h-xs w-xs cursor-pointer rounded-md', {
                        'bg-border-black': index === currentSlide,
                        'bg-border-inverse': index !== currentSlide,
                    })}
                    data-testid="slider-dot"
                    key={index}
                    onClick={() => {
                        setPreviousSlide(currentSlide);
                        setCurrentSlide(index);
                    }}
                />
            );
        }
        return dots;
    };
    // #endregion dots

    // #region auto scroll
    useEffect(() => {
        if (autoScroll && !isHovering) {
            autoScrollInterval.current = setInterval(() => {
                let slide = currentSlide + 1;
                if (slide > slides.length - 1) {
                    slide = 0;
                }
                setPreviousSlide(currentSlide);
                setCurrentSlide(slide);
            }, autoScroll);
            return () => clearInterval(autoScrollInterval.current);
        }
    }, [autoScroll, currentSlide, isHovering, slides.length]);
    // #endregion auto scroll

    // if there's no slider elements, no reason to render
    if (!children.length) {
        return null;
    }

    return (
        <div
            className="relative h-full w-full"
            data-testid="slider"
            ref={sliderRef}
        >
            {hasDots && <div className="absolute bottom-[0.5px] z-[1] w-full text-center">{dots()}</div>}
            <div className="relative h-full w-full overflow-hidden">
                {springProps.map(({ offset, opacity }, index) => {
                    return (
                        <animated.div
                            className="absolute h-full w-full"
                            data-testid="slider-slide"
                            {...gestureBinds()}
                            key={index}
                            style={{
                                opacity: opacity,
                                transform: offset.to((offset) => `translate3d(${offset * 100}%,0,0)`),
                                touchAction: 'pan-y',
                                userSelect: 'none',
                                willChange: 'transform',
                            }}
                        >
                            {slides[index]}
                        </animated.div>
                    );
                })}
            </div>
        </div>
    );
};
